import crawlPolicyData from "../data/crawl-policy-data.json" with { type: "json" };
import { LEGACY_CRAWL_CANONICAL_PATHS } from "./canonical-redirects";
import { PRIVATE_CRAWL_PREFIXES } from "./private-route-policy";
import { legacyCanonicalPath } from "../../edge-db/shared/internal-href-canonical";

// Removed templated families answer 410 Gone; the rule table and its rationale
// live in the dependency-free module the static exporter also imports.
export { goneFamilyHubPath, isGonePath } from "../../edge-db/shared/gone-paths";

const QUERY_CANONICAL_EXEMPT_PREFIXES = [
  "/api",
  "/checkout",
  "/checkouts",
  "/ops",
  "/pay",
] as const;

function canonicalPath(pathname: string) {
  if (pathname === "/") return "/";
  if ((pathname.split("/").pop() ?? "").includes(".")) return pathname;
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Non-content utility surfaces: iframe embeds, widget endpoints and the app-store
 * redirect. These are not pages and must not enter the index. Hardcoded on purpose —
 * there is no SEO policy layer any more. Delete this list to index them.
 *
 * Also the regional `/<kn|ml|te>/free-kundali/` editions. On 2026-09-23
 * (sitemap audit) all seven regional editions were listed here: each was ~250
 * words of translated intro and FAQ around the English-only kundali form,
 * promising a chart "in Bengali/Tamil/…" the tool could not produce, and they
 * earned 12 impressions in total over Jul–Sep. The exit rule was: remove a path
 * once its edition renders the form and the chart in its own language.
 *
 * bn, gu, mr, or and ta met it on 2026-10-21 (`IN_LANGUAGE_KUNDALI_LOCALES`):
 * their form, chart, planet table, dasha and dosha names render natively, so
 * they are indexed, sitemapped under their own shard and in the free-kundali
 * hreflang cluster. kn, ml and te still wrap the English form, so they stay
 * served and linked (`noindex, follow`), and `regionalLanguageEntries` keeps
 * them out of every cluster. `tests/regional-edition-indexability.test.ts`
 * holds this list to exactly the editions outside `IN_LANGUAGE_KUNDALI_LOCALES`.
 */
const PUBLIC_NOINDEX_PATHS = new Set<string>(crawlPolicyData.publicNoindexExactPaths);

/**
 * Whole public sections served `noindex, follow`, matched on a path segment:
 * `/products` covers `/products` and `/products/<anything>`, never `/productsx/`.
 * Unlike `privateCrawlPrefixes` these stay `follow` and keep their links.
 *
 * `/products` is the parked Shopify commerce lane (owner decision 2026-10-04).
 * The `/products/[handle]` route was removed on 2026-10-26 (ADR-0122 physical
 * closeout), so no product page is served; the prefix stays so any stray path in
 * the lane reads `noindex, follow`. If the shop ever relaunches, delete the prefix
 * and give the new cohort a sitemap projection. A redirect or 410 rule still wins,
 * because `pathDisposition` resolves those first: `/products/` and five retired
 * handles answer 410 (`shopify-leftover`), and the three karungali handles 301 to
 * the karungali editorial guide.
 *
 * A prefix match is faithful for a `[handle]` probe: the completeness gate's
 * `/products/probe/` stand-in reads the same disposition as every real handle.
 * Node and Python readers mirror this (scripts/crawl-policy-reader.mjs,
 * scripts/served-noindex-population.mjs, execution/_governed_noindex.py).
 */
const PUBLIC_NOINDEX_PREFIXES: readonly string[] = crawlPolicyData.publicNoindexPrefixes;

export function isPublicNoindexPath(pathname: string) {
  const normalized = canonicalPath(pathname);
  return (
    PUBLIC_NOINDEX_PATHS.has(normalized) ||
    PUBLIC_NOINDEX_PREFIXES.some((prefix) => matchesPrefix(normalized, prefix))
  );
}

export function isPrivateCrawlPath(pathname: string) {
  return PRIVATE_CRAWL_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

/** @internal */
export function isQueryCanonicalizationExemptPath(pathname: string) {
  return QUERY_CANONICAL_EXEMPT_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

/**
 * Pattern-shaped legacy redirects, for families too large to enumerate as exact
 * paths (year-qualified Rashifal/transit URLs, muhurat month slices, and counting).
 *
 * These belong in the SAME authority as the exact map because `pathDisposition`
 * resolves `redirect` before every noindex branch: a path claimed here can never
 * also be claimed as `noindex,follow`. Before 2026-07-30 the muhurat months were
 * declared BOTH ways — a noindex pattern list (removed 2026-09-30) said serve-200-noindex while
 * `middleware.ts` 301'd them — and the static publisher believed the contract, so
 * it seeded 169 URLs it then fetched as HTTP 301. That single disagreement was
 * 57% of a quarantine that blocked every publish for two days. The same table
 * also keeps app middleware, both content Workers, sitemap filtering, and R2
 * pruning aligned when a retired year-qualified object still exists in storage.
 */
export function legacyCrawlCanonicalPath(pathname: string) {
  return legacyCanonicalPath(pathname, LEGACY_CRAWL_CANONICAL_PATHS);
}

/** @internal */
export function legacyPolicyCanonicalPath(pathname: string) {
  const canonical = legacyCrawlCanonicalPath(pathname);
  return pathname.startsWith("/policies/") ? canonical : null;
}

/** @internal */
export function shouldInjectRouteJsonLd(pathname: string) {
  const normalized = canonicalPath(pathname);
  return !isPrivateCrawlPath(normalized) && !isPublicNoindexPath(normalized);
}
